import { useState, useContext, useEffect } from 'react';
import { Link, Chip, Box, Tooltip } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import EmailIcon from '@mui/icons-material/Email';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppContext, SttButtonFab, SttPage } from '../../../sporttia/all';
import { fullName, checkOffTag } from '../../../lib/utils';
import { sporttiaColors } from '../../../theme/theme';
import { useInteractionsFiles, useQueryFilters } from '../../../lib/hooks';
import { SPORTCENTER_USERS } from '../../../lib/queryKeys';
import SttCachedTable from '../../../sporttia/SttCachedTable';
import useScsService from '../../../services/ScsService';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import UserCreationDialog from '../../../components/dialogs/UserCreationDialog';
import PhotoViewerDialog from '../../../components/dialogs/PhotoViewerDialog';
import Mship from './Mship';
import MshipsFilterDialog from './MshipsFilterDialog';
import { formatMshipPurseAmount } from './formatMshipPurseAmount';
import { useMshipsTour } from 'src/lib/tours/useMshipsTour';
import { PERMISSIONS } from '../../../lib/permissions';

type StatusIconProps = {
    active: boolean;
    ActiveIcon: SvgIconComponent;
    InactiveIcon: SvgIconComponent;
    activeLabel: string;
    inactiveLabel: string;
};

/**
 * Present data: filled icon on the confirmation surface/ink pair. Absent data:
 * outlined icon in `--muted-foreground`, no surface. Fill, shape and tooltip
 * all change, so the state never rests on hue alone.
 */
function StatusIcon({
    active,
    ActiveIcon,
    InactiveIcon,
    activeLabel,
    inactiveLabel,
}: StatusIconProps) {
    const Icon = active ? ActiveIcon : InactiveIcon;

    return (
        <Tooltip title={active ? activeLabel : inactiveLabel}>
            <Box
                aria-label={active ? activeLabel : inactiveLabel}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: active ? 'var(--ok-sf)' : 'transparent',
                }}
            >
                <Icon
                    sx={{
                        fontSize: 18,
                        color: active
                            ? 'var(--ok-ink)'
                            : sporttiaColors.icon.disabled,
                    }}
                />
            </Box>
        </Tooltip>
    );
}

type FilterState = {
    name?: string;
    login?: string;
    email?: string;
    gender?: string | null;
    minPurseAmount?: number | null;
    minDebt?: number | null;
    certified?: string | null;
    idSport?: number | null;
    idGroup?: number;
    dni?: string;
    idCity?: number;
    postalCode?: string | null;
    mobile?: string;
    minAge?: number | null;
    maxAge?: number | null;
    group?: { id: number };
    city?: { id: number };
    trash?: boolean;
    keyword?: string;
};

type MshipRowData = {
    id: number;
    fullName?: string;
    img?: { url: string };
    user: {
        login: string;
    };
    age?: number;
    tutor?: {
        name?: string;
        surname1?: string;
        surname2?: string;
    };
    notes?: string;
    certified?: boolean | null;
    email?: string | null;
    mobile?: string | null;
    numBookings: number;
    amount: number;
    debt: number;
    total: number;
};

export default function Mships(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { startTour } = useMshipsTour();
    const scsService = useScsService();
    const queryClient = useQueryClient();
    const { downloadFile } = useInteractionsFiles();
    const { id: urlId, tab: urlTab } = useParams<{
        id: string;
        tab?: string;
    }>();
    const navigate = useNavigate();
    const [mshipId, setMshipId] = useState<number | null>(null);
    const [filters, setFilters] = useQueryFilters<FilterState>(
        {},
        SPORTCENTER_USERS,
    );
    const [creatingUserDialogOpen, setCreatingUserDialogOpen] =
        useState<boolean>(false);
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
    const [selectedPhotoUrl, setSelectedPhotoUrl] = useState<string | null>(
        null,
    );
    const [initialTab, setInitialTab] = useState<number | undefined>(undefined);

    // Handle mship ID from URL path parameter (e.g., /mships/123)
    // Also clear mshipId when urlId becomes undefined (e.g., browser back button)
    useEffect(() => {
        if (urlId) {
            setMshipId(Number(urlId));
        } else {
            setMshipId(null);
        }
    }, [urlId]);

    /**
     * Handle close of mship dialog
     */
    const handleMshipClose = () => {
        setMshipId(null);
        setInitialTab(undefined);
        queryClient.invalidateQueries({
            queryKey: [SPORTCENTER_USERS],
        });
        // Navigate back to mships list if we came from a direct URL
        if (urlId) {
            navigate('/mships');
        }
    };

    /**
     * Método para descargar los ficheros de los informes.
     * TODO migrar a react-query
     * @param format [pdf|csv] string format.
     */
    const download = (format: string) => {
        const urlQuery = Object.keys(filters)
            .filter(
                (key: any) =>
                    (filters as Record<string, unknown>)[key] !== null,
            )
            .map(
                (key: any) =>
                    `${key}=${(filters as Record<string, unknown>)[key]}`,
            )
            .join('&');

        downloadFile(
            `/scs/${cxt?.sc?.id}/mships.${format}?${urlQuery}`,
            format === 'pdf' ? 'application/pdf' : 'text/csv',
            undefined as any,
            `users.${format}`,
        );
    };

    /**
     * In this case, the value of group and city is changed to idGroup and idCity.
     * @param updatedFilters Object with the parameters returned by the filter dialog.
     */
    const doFiltering = (updatedFilters: FilterState) => {
        const formatedFilters: FilterState = { ...updatedFilters };

        delete formatedFilters.group;
        delete formatedFilters.city;

        if (updatedFilters?.group) {
            formatedFilters.idGroup = updatedFilters?.group?.id;
        }

        if (updatedFilters?.city) {
            formatedFilters.idCity = updatedFilters?.city?.id;
        }

        setFilters(formatedFilters);
    };

    /**
     * Handle avatar click to open photo viewer
     */
    const handleAvatarClick = (row: MshipRowData) => {
        if (row.img?.url) {
            setSelectedPhotoUrl(row.img.url);
            setPhotoViewerOpen(true);
        }
    };

    return (
        <SttPage title={t('users')} help={startTour}>
            <SttTopFilteringControls
                p={2}
                fields={[
                    {
                        type: 'search',
                        name: 'keyword',
                        caption: t('generic.search'),
                        value: filters.keyword || '',
                        xs: 12,
                        sm: 12,
                        md: 6,
                        lg: 4,
                    },
                ]}
                filterButton={{
                    onClick: () => setFilterDialogOpen(true),
                }}
                trashAction
                onFilter={doFiltering}
                menu={[
                    {
                        caption: `${t('generic.download')} PDF`,
                        onClick: () => download('pdf'),
                    },
                    {
                        caption: `${t('generic.download')} CSV`,
                        onClick: () => download('csv'),
                    },
                ]}
            />

            {/* Filter Dialog */}
            <MshipsFilterDialog
                open={filterDialogOpen}
                onClose={() => setFilterDialogOpen(false)}
                onApply={doFiltering as any}
                initialFilters={filters}
            />

            <SttCachedTable
                queryKey={SPORTCENTER_USERS}
                queryFn={(params: any) =>
                    scsService.getScsMships(Number(cxt?.sc?.id) || 0, params)
                }
                queryParams={filters}
                columns={[
                    {
                        type: 'avatar',
                        value: (row: MshipRowData) =>
                            row.img ? row.img.url : null,
                        onClick: handleAvatarClick,
                    },
                    {
                        field: 'name',
                        title: t('Name'),
                        align: 'left',
                        value: (r: MshipRowData) => (
                            <>
                                <Link
                                    onClick={() => setMshipId(r.id)}
                                    sx={{
                                        // `text.disabled` daba 2,68:1: el
                                        // usuario está desactivado, pero su
                                        // nombre sigue siendo un enlace que se
                                        // pulsa. Con el gris de texto son
                                        // 6,2:1 y se sigue distinguiendo del
                                        // resto, que va en lima de texto.
                                        color: checkOffTag(
                                            (r.user as any).login,
                                        )
                                            ? 'text.secondary'
                                            : 'var(--brand-text)',
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    {r.fullName
                                        ? r.fullName
                                        : t('UserWithoutName')}
                                </Link>

                                <br />
                                {r.user &&
                                    (checkOffTag((r.user as any).login) ? (
                                        <Box
                                            component="i"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            {t('UserCancelled')}
                                        </Box>
                                    ) : (
                                        `@${(r.user as any).login}`
                                    ))}
                                {(r.age ?? 0) > 0 && (
                                    <span>
                                        ,{' '}
                                        {r.age
                                            ? `${r.age} ${t('generic.years')}`
                                            : ''}
                                    </span>
                                )}
                                {r.tutor && (
                                    <Box mt={1}>
                                        <Chip
                                            size="small"
                                            label={`${t(
                                                'Tutor',
                                            )}: ${fullName(r.tutor)}`}
                                        />
                                    </Box>
                                )}

                                {r.notes && (
                                    <Box mt={1} sx={{ color: 'error.main' }}>
                                        {r.notes}
                                    </Box>
                                )}
                            </>
                        ),
                    },
                    {
                        field: 'status',
                        title: t('Status'),
                        align: 'center',
                        value: (r: MshipRowData) => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 0.5,
                                    justifyContent: 'center',
                                }}
                            >
                                <StatusIcon
                                    active={
                                        r.certified !== undefined &&
                                        r.certified !== null
                                    }
                                    ActiveIcon={VerifiedUserIcon}
                                    InactiveIcon={VerifiedUserOutlinedIcon}
                                    activeLabel={t('Certified')}
                                    inactiveLabel={t('NotCertified')}
                                />
                                <StatusIcon
                                    active={r.email !== null}
                                    ActiveIcon={EmailIcon}
                                    InactiveIcon={MailOutlineIcon}
                                    activeLabel={t('generic.email')}
                                    inactiveLabel={t('NoEmail')}
                                />
                                <StatusIcon
                                    active={!!r.mobile}
                                    ActiveIcon={PhoneAndroidIcon}
                                    InactiveIcon={PhoneAndroidOutlinedIcon}
                                    activeLabel={t('generic.mobile')}
                                    inactiveLabel={t('NoPhone')}
                                />
                            </Box>
                        ),
                    },
                    {
                        field: 'numBookings',
                        title: t('bookings'),
                        type: 'counter',
                        align: 'right',
                    },
                    {
                        field: 'amount',
                        title: t('generic.purse'),
                        align: 'right',
                        value: (row: MshipRowData) => (
                            <Link
                                sx={{
                                    color: 'var(--brand-text)',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                                onClick={() => {
                                    setInitialTab(2);
                                    setMshipId(row.id);
                                }}
                            >
                                {formatMshipPurseAmount(
                                    row.amount,
                                    cxt?.sc?.city?.country?.currency,
                                )}
                            </Link>
                        ),
                    },
                    {
                        field: 'debt',
                        title: t('Debt'),
                        type: 'price',
                        align: 'right',
                        value: (row: MshipRowData) => ({
                            paid: false,
                            price: row.debt,
                        }),
                    },
                    {
                        field: 'total',
                        title: t('Total'),
                        type: 'price',
                        align: 'right',
                    },
                ]}
            />

            <Mship
                id={mshipId as number}
                open={!!mshipId}
                initialTab={initialTab}
                initialTabKey={urlTab}
                onClose={handleMshipClose}
            />

            <UserCreationDialog
                open={creatingUserDialogOpen}
                onClose={() => {
                    setCreatingUserDialogOpen(false);
                }}
                onCreate={() => {
                    queryClient.invalidateQueries({
                        queryKey: [SPORTCENTER_USERS],
                    });
                }}
            />

            <SttButtonFab
                onClick={() => setCreatingUserDialogOpen(true)}
                permission={PERMISSIONS.CUSTOMER.CREATE}
            />

            <PhotoViewerDialog
                open={photoViewerOpen}
                onClose={() => {
                    setPhotoViewerOpen(false);
                    setSelectedPhotoUrl(null);
                }}
                photoUrl={selectedPhotoUrl}
                altText={t('userPhoto')}
            />
        </SttPage>
    );
}
